import { actions, assign, createMachine, InvokeCallback, send } from 'xstate';
import { isRequestMessage } from '../helpers';
import {
  API_BASE,
  DoneMessage,
  Message,
  RequestMessage,
  ServiceID,
  Version,
} from '../types';
import { requestStop, sendResponse } from './actions';

let { pure } = actions;

type Context = {
  id: Version;
  queue: Message[];
};

function buildURL(
  message: RequestMessage
): `${API_BASE}/${ServiceID}/${Version}` {
  return `${message.apiBase}/${message.serviceID}/${message.version}`;
}
function buildInit(message: RequestMessage): RequestInit {
  let method = 'GET';
  if (message.type === 'delete') method = 'DELETE';
  if (message.type === 'save') method = 'POST';
  let init: RequestInit = {
    method,
    headers: {
      'x-caller-id': message.xCallerId,
    },
  };
  if (message.type === 'save') {
    init.body = JSON.stringify(message.data);
  }
  return init;
}

function getCallback(
  message: RequestMessage
): InvokeCallback<RequestMessage, DoneMessage> {
  return (callback) => {
    fetch(buildURL(message), buildInit(message))
      .then((response) => response.json())
      .catch((error) => ({ code: 'ERROR_INTERNAL', message: error.message }))
      .then((response) =>
        callback({ type: 'done', messageID: message.messageID, response })
      );
  };
}

export let request = createMachine<Context, Message>(
  {
    id: 'version_request',
    meta: {
      description:
        __DEV__ &&
        'Отвечает за очередь запросов к одной версии черновика в рамках одной услуги',
    },
    context: {
      id: '',
      queue: [],
    },
    initial: 'idle',
    states: {
      idle: {
        after: {
          1000: { actions: ['request_stop'] },
        },
        on: {
          save: {
            target: 'working',
          },
          load: {
            target: 'working',
          },
          delete: {
            target: 'working',
          },
        },
      },
      working: {
        invoke: { src: 'request_callback' },
        on: {
          save: { actions: ['put_into_queue'] },
          load: { actions: ['put_into_queue'] },
          delete: { actions: ['put_into_queue'] },
          done: { target: 'complete' },
        },
      },
      complete: {
        entry: ['send_response'],
        always: [
          { actions: ['send_next_from_queue'], cond: 'queue_not_empty' },
          { target: 'idle' },
        ],
        on: {
          save: {
            target: 'working',
          },
          load: {
            target: 'working',
          },
          delete: {
            target: 'working',
          },
        },
      },
    },
  },
  {
    guards: {
      queue_not_empty: (ctx) => ctx.queue.length > 0,
    },
    actions: {
      put_into_queue: assign({
        queue: (ctx, message) => ctx.queue.concat(message),
      }),
      remove_queue_head: assign({
        queue: (ctx) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          let [_, ...tail] = ctx.queue;
          return tail;
        },
      }),
      send_next_from_queue: pure((ctx) => {
        let [message, ...rest] = ctx.queue;
        return [
          assign<Context, Message>({ queue: rest }),
          send<Context, Message>(message),
        ];
      }),
      send_response: sendResponse,
      request_stop: requestStop,
    },
    services: {
      request_callback(_, message) {
        isRequestMessage(message);
        return getCallback(message);
      },
    },
  }
);
